// Order updates (migration 055) --- the PO panel's Updates tab: what the vendor says about the order (the portal's, from
// them) and the same forms for HHL to enter it for a vendor not on the portal (ui/OrderUpdates.tsx): confirmed, progress,
// a new ship date, shipments with what's in them, notes. Built from synced rows, so it's live. Opening it marks the
// vendor's updates seen (the badge on Purchase orders and the Updates tab).
import { useEffect, useMemo } from "react";
import type { Po, PoLine } from "../../../../shared/db.gen.ts";
import type { ShipmentView, UpdateView } from "../../../../shared/order-updates.ts";
import { personName } from "../../lib/format.ts";
import { can, mutate } from "../../lib/mutate.ts";
import { Link } from "../../lib/router.tsx";
import { useRows } from "../../lib/sync.ts";
import { ORDER_TEXT_STAFF, type OrderParts, OrderUpdates, type OrderView } from "../../ui/OrderUpdates.tsx";
import { toast } from "../../ui/toast.tsx";

const OPEN = ["sent", "acknowledged", "in_production", "shipped"];

/** Vendor updates nobody at HHL has opened yet, per PO. */
export function useUnseenUpdates(): Map<string, number> {
  const updates = useRows("po_update");
  return useMemo(() => {
    const m = new Map<string, number>();
    for (const u of updates) if (u.contact_id && !u.seen_at) m.set(u.po_id, (m.get(u.po_id) ?? 0) + 1);
    return m;
  }, [updates]);
}

/** The order as both sides read it, from synced rows. */
function useOrderView(po: Po, lines: PoLine[]): OrderView {
  const items = useRows("item");
  const makeOrders = useRows("make_order");
  const shipments = useRows("po_shipment");
  const shipmentLines = useRows("po_shipment_line");
  const updates = useRows("po_update");
  const contacts = useRows("vendor_contact");
  const users = useRows("app_user");
  return useMemo(() => {
    const item = new Map(items.map((i) => [i.id, i]));
    const line = new Map(lines.map((l) => [l.id, l]));
    const sku = (l: PoLine) => l.sku || item.get(l.item_id)?.sku || "";
    const by = (contactId: string | null, userId: string | null) => {
      if (!contactId) return personName(users, userId);
      const c = contacts.find((x) => x.id === contactId);
      return c ? c.name || c.email : "Them";
    };
    const mine = shipments
      .filter((s) => s.po_id === po.id)
      .sort((a, b) => b.shipped_on.localeCompare(a.shipped_on) || b.created_at.localeCompare(a.created_at));
    return {
      number: po.number,
      status: po.status,
      open: OPEN.includes(po.status),
      expected_ship: po.expected_ship,
      vendor_ship_date: po.vendor_ship_date,
      vendor_ref: po.vendor_ref,
      lines: lines.map((l) => ({
        id: l.id,
        sku: sku(l),
        name: l.name || item.get(l.item_id)?.name || "",
        vendor_sku: l.vendor_sku,
        qty: Number(l.qty),
        qty_shipped: Number(l.qty_shipped),
        qty_received: Number(l.qty_received),
        qty_ready: Number(l.qty_ready),
        vendor_date: l.vendor_date,
        promised_date: l.promised_date,
      })),
      builds: makeOrders
        .filter((m) => m.po_id === po.id)
        .map((m) => ({
          id: m.id,
          po_line_id: m.po_line_id,
          sku: item.get(m.item_id)?.sku ?? "",
          name: item.get(m.item_id)?.name ?? "",
          qty: Number(line.get(m.po_line_id)?.qty ?? 0),
          made: Number(m.qty_output),
          stage: m.stage,
        })),
      shipments: mine.map(
        (s): ShipmentView => ({
          id: s.id,
          shipped_on: s.shipped_on,
          carrier: s.carrier,
          tracking_number: s.tracking_number,
          boxes: s.boxes,
          note: s.note,
          from_them: !!s.contact_id,
          by_name: by(s.contact_id, s.created_by),
          received_at: s.received_at,
          lines: shipmentLines
            .filter((x) => x.shipment_id === s.id)
            .map((x) => {
              const l = line.get(x.po_line_id);
              return { po_line_id: x.po_line_id, sku: l ? sku(l) : "", name: l?.name ?? "", qty: Number(x.qty), position: l?.position ?? 0 };
            })
            .sort((a, b) => a.position - b.position),
        }),
      ),
      updates: updates
        .filter((u) => u.po_id === po.id)
        .sort((a, b) => b.created_at.localeCompare(a.created_at))
        .map(
          (u): UpdateView => ({
            id: u.id,
            kind: u.kind,
            note: u.note,
            new_date: u.new_date,
            detail: (u.detail ?? {}) as Record<string, unknown>,
            from_them: !!u.contact_id,
            by_name: by(u.contact_id, u.created_by),
            created_at: u.created_at,
          }),
        ),
    };
  }, [po, lines, items, makeOrders, shipments, shipmentLines, updates, contacts, users]);
}

export function PoUpdates({ po, lines, parts }: { po: Po; lines: PoLine[]; parts?: OrderParts }) {
  const order = useOrderView(po, lines);
  const unseen = useUnseenUpdates().get(po.id) ?? 0;
  useEffect(() => {
    if (!parts && unseen > 0 && can("po_update.seen")) void mutate("po_update.seen", { po_id: po.id });
  }, [parts, unseen, po.id]);
  // mutate() shows the server's refusal itself; a throw keeps the form open with what was typed.
  const run = async (ok: Promise<boolean>) => {
    if (!(await ok)) throw new Error("");
  };
  const writes = can("po.vendor_progress");
  return (
    <div className="min-h-0 flex-1 overflow-y-auto p-4">
      {!parts && (
        <p className="mb-3 text-[12px] text-muted">
          What {po.number}'s vendor says about it: from them in the vendor portal, or entered here for them (a vendor not on the portal, or an email they sent).
        </p>
      )}
      <OrderUpdates
        parts={parts}
        order={{ ...order, open: order.open && writes }}
        text={ORDER_TEXT_STAFF}
        onError={(m) => m && toast.error(m)}
        actions={{
          confirm: (i) => run(mutate("po.vendor_confirm", { id: po.id, update_id: crypto.randomUUID(), ...i })),
          progress: (i) => run(mutate("po.vendor_progress", { id: po.id, update_id: crypto.randomUUID(), ...i })),
          newDate: (i) => run(mutate("po.vendor_new_date", { id: po.id, update_id: crypto.randomUUID(), ...i })),
          note: (note) => run(mutate("po.vendor_note", { id: po.id, update_id: crypto.randomUUID(), note })),
          ship: (i) => run(mutate("po_shipment.add", { id: crypto.randomUUID(), po_id: po.id, ...i })),
          removeShipment: writes ? (id) => run(mutate("po_shipment.remove", { id })) : undefined,
          canRemove: () => true,
          extra: (s) =>
            !s.received_at && OPEN.includes(po.status) ? (
              <Link to={`/receiving?po=${po.id}`} className="text-accent-text hover:underline">
                Receive it
              </Link>
            ) : null,
        }}
      />
    </div>
  );
}
